import { useState, useRef } from "react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { UploadCloud, Download, FileSpreadsheet, Loader2 } from "lucide-react";
import { api, importTemplateUrl } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { EmployeeFieldsImport } from "./EmployeeFieldsImport";

export const ImportDialog = ({ open, onOpenChange, onImported }) => {
  const { t, dir } = useLang();
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef();

  const reset = () => { setFile(null); setResult(null); };

  const handleImport = async () => {
    if (!file) { toast.error(t("dash.modals.import.chooseFirst")); return; }
    setBusy(true);
    try {
      const res = await api.importEmployees(file);
      setResult(res);
      toast.success(`${t("dash.modals.import.addedN", { n: res.created })}${res.updated ? ` · حُدّث ${res.updated}` : ""}${res.skipped ? ` · تُخطّي ${res.skipped} مكرر` : ""}`);
      onImported();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("dash.modals.import.importFail"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="sm:max-w-md font-body" dir={dir}>
        <DialogHeader className="text-start">
          <DialogTitle className="font-heading text-xl">{t("dash.modals.import.title")}</DialogTitle>
          <DialogDescription className="text-start">
            {t("dash.modals.import.desc")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <EmployeeFieldsImport onDone={onImported} />
          <a href={importTemplateUrl} data-testid="download-template-link"
            className="inline-flex items-center gap-2 text-sm font-medium text-blue-700 hover:underline">
            <Download className="h-4 w-4" /> {t("dash.modals.import.downloadTemplate")}
          </a>

          <label data-testid="import-dropzone"
            className="flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-slate-300 p-6 cursor-pointer hover:bg-slate-50 transition">
            <UploadCloud className="h-8 w-8 text-slate-400" />
            {file ? (
              <span className="flex items-center gap-2 text-sm text-slate-700"><FileSpreadsheet className="h-4 w-4" /> {file.name}</span>
            ) : (
              <span className="text-sm text-slate-500">{t("dash.modals.import.pickFile")}</span>
            )}
            <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" className="hidden"
              data-testid="import-file-input"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }} />
          </label>

          {result && (
            <div data-testid="import-result" className="rounded-xl bg-blue-50 border border-blue-200 p-3 text-sm text-blue-800">
              {t("dash.modals.import.resultAdded", { n: result.created })}
              {result.errors?.length > 0 && (
                <span className="block text-amber-700 mt-1">{t("dash.modals.import.errorsN", { n: result.errors.length })}</span>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
          <Button data-testid="confirm-import-button" disabled={busy || !file}
            className="bg-blue-600 hover:bg-blue-700 active:scale-95 transition gap-2" onClick={handleImport}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
            {busy ? t("dash.modals.import.importing") : t("dash.modals.import.doImport")}
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("dash.modals.import.close")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
